import React, { useRef, useState, useEffect } from "react";
import {
  Container,
  Typography,
  Chip,
  Grid,
  Box,
  Button,
  Paper,
  TextField,
  Avatar,
  InputAdornment,
  Fade,
  Zoom,
  Grow,
  Stack,
  Alert,
  Snackbar,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  CircularProgress,
} from "@mui/material";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as BirthdayIcon,
  Save as SaveIcon,
  Loyalty as LoyaltyIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Restaurant as RestaurantIcon,
  LocalBar as DrinkIcon,
  DinnerDining as DiningIcon,
  QrCode as QrCodeIcon,
  Download as DownloadIcon,
  MenuBook as MenuBookIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import { keyframes } from "@emotion/react";
import backgroundPattern from "../assets/bg.PNG";

// API Base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Modern color palette
const colors = {
  primary: {
    main: "#2A5C4A",
    light: "#3B7A63",
    dark: "#1E4035",
    gradient: "linear-gradient(135deg, #2A5C4A 0%, #3B7A63 100%)",
  },
  secondary: {
    main: "#D4A373",
    light: "#E9C46A",
    dark: "#B5835A",
    gradient: "linear-gradient(135deg, #D4A373 0%, #E9C46A 100%)",
  },
  accent: {
    red: "#B22222",
    emerald: "#4ECDC4",
  },
  background: {
    dark: "#0F1A16",
    paper: "#1E2C26",
    overlay: "rgba(15, 26, 22, 0.95)",
  },
  text: {
    primary: "#FFFFFF",
    secondary: "#B8C5C0",
    muted: "#8A9D95",
  },
};

// Animation keyframes
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { opacity: 0.6; transform: scale(0.98); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.6; transform: scale(0.98); }
`;

const MainContent = ({ qrMenuURL = "https://loyalty.myelegante.com" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const qrRef = useRef();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loyaltyForm, setLoyaltyForm] = useState({
    name: "",
    phone: "",
    email: "",
    dob: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [members, setMembers] = useState([]);
  const [hoveredField, setHoveredField] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [stats, setStats] = useState({
    total_members: 0,
    total_points: 0,
    new_members_this_month: 0
  });

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Load data from API on component mount
  useEffect(() => {
    loadMembers();
    loadStats();
  }, []);

  // API Functions
  const loadMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/loyalty.php?action=get_members`);
      const result = await response.json();
      
      if (result.success) {
        setMembers(result.data);
        localStorage.setItem('greenVelvetMembers', JSON.stringify(result.data));
      } else {
        setApiError(result.message);
      }
    } catch (error) {
      console.error('Error loading members:', error);
      setApiError('Failed to load members. Please check your connection.');
      
      // Fallback to localStorage if API fails
      const savedMembers = localStorage.getItem('greenVelvetMembers');
      if (savedMembers) {
        setMembers(JSON.parse(savedMembers));
      }
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/loyalty.php?action=get_stats`);
      const result = await response.json();
      
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const checkMemberExists = async (phone, email) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/loyalty.php?action=check_member&phone=${encodeURIComponent(phone)}&email=${encodeURIComponent(email)}`
      );
      const result = await response.json();
      return result.data.exists;
    } catch (error) {
      console.error('Error checking member:', error);
      return false;
    }
  };

  const enrollMember = async (memberData) => {
    const response = await fetch(`${API_BASE_URL}/loyalty.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'enroll_member',
        ...memberData
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoyaltyForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    // Clear API error when user starts typing
    if (apiError) setApiError(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!loyaltyForm.name.trim()) errors.name = "Name is required";
    if (!loyaltyForm.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^(0|234)[789][01]\d{8}$/.test(loyaltyForm.phone.replace(/\s/g, ''))) {
      errors.phone = "Enter a valid Nigerian phone number";
    }
    if (!loyaltyForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(loyaltyForm.email)) {
      errors.email = "Enter a valid email address";
    }
    if (!loyaltyForm.dob) {
      errors.dob = "Date of birth is required";
    } else {
      const age = new Date().getFullYear() - new Date(loyaltyForm.dob).getFullYear();
      if (age < 18) {
        errors.dob = "You must be 18 or older";
      }
    }
    return errors;
  };

  const handleEnroll = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      setSubmitting(true);
      setApiError(null);
      
      try {
        // Check if member already exists
        const exists = await checkMemberExists(loyaltyForm.phone, loyaltyForm.email);
        
        if (exists) {
          setApiError("A member with this phone or email already exists");
          setSubmitting(false);
          return;
        }

        // Enroll new member
        const result = await enrollMember(loyaltyForm);
        
        if (result.success) {
          const newMember = {
            ...loyaltyForm,
            points: result.data.points,
            joinDate: new Date().toISOString(),
            id: result.data.id,
          };
          
          const updatedMembers = [...members, newMember];
          setMembers(updatedMembers);
          localStorage.setItem('greenVelvetMembers', JSON.stringify(updatedMembers));
          
          setLoyaltyPoints(result.data.points);
          setShowSuccess(true);
          
          // Reset form
          setLoyaltyForm({
            name: "",
            phone: "",
            email: "",
            dob: "",
          });

          // Refresh stats
          loadStats();
        } else {
          setApiError(result.message || "Failed to enroll member");
        }
      } catch (error) {
        console.error('Error enrolling member:', error);
        setApiError('Network error. Please check your connection and try again.');
      } finally {
        setSubmitting(false);
      }
    } else {
      setFormErrors(errors);
    }
  };

  const handleDownloadQR = async () => {
    try {
      const canvas = await html2canvas(qrRef.current);
      const link = document.createElement("a");
      link.download = "green-velvet-loyalty-qr.png";
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error downloading QR:', error);
      setApiError('Failed to download QR code');
    }
  };

  const handleViewMenu = () => {
    window.open("https://greenvelvet.myelegante.com/", "_blank", "noopener,noreferrer");
  };

  // Loading Screen
  if (loading) {
    return (
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `radial-gradient(circle at 50% 50%, ${colors.background.paper} 0%, ${colors.background.dark} 100%)`,
          overflow: "hidden",
        }}
      >
        {/* Animated background particles */}
        {[...Array(20)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: colors.secondary.main,
              opacity: 0.2,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `${pulse} ${3 + Math.random() * 2}s infinite`,
              transform: `scale(${0.5 + Math.random()})`,
            }}
          />
        ))}

        <Fade in={true} timeout={1000}>
          <Box sx={{ textAlign: "center", position: "relative" }}>
            <Box
              sx={{
                position: "relative",
                width: 180,
                height: 180,
                margin: "0 auto",
                animation: `${spin} 3s linear infinite`,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "50%",
                  // Changed to green-only gradient
                  background: `conic-gradient(from 0deg, ${colors.primary.main}, ${colors.primary.light}, ${colors.primary.dark}, ${colors.primary.main})`,
                  mask: "radial-gradient(transparent 55%, black 56%)",
                  WebkitMask: "radial-gradient(transparent 55%, black 56%)",
                }}
              />
              
              <Box
                sx={{
                  position: "absolute",
                  top: "15%",
                  left: "15%",
                  right: "15%",
                  bottom: "15%",
                  borderRadius: "50%",
                  background: colors.background.dark,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  animation: `${float} 2s ease-in-out infinite`,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.2rem", md: "1.4rem" },
                    letterSpacing: 2,
                    background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 0.5,
                  }}
                >
                  GREEN
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.2rem", md: "1.4rem" },
                    letterSpacing: 2,
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  VELVET
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{
                mt: 4,
                color: colors.text.secondary,
                fontSize: "0.9rem",
                letterSpacing: 2,
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            >
              PREPARING YOUR EXPERIENCE
            </Typography>
          </Box>
        </Fade>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        background: `radial-gradient(circle at 50% 50%, ${colors.background.paper} 0%, ${colors.background.dark} 100%)`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backgroundPattern})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.08,
          zIndex: 0,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 30%, ${alpha(colors.primary.main, 0.05)} 0%, transparent 50%),
                      radial-gradient(circle at 80% 70%, ${alpha(colors.secondary.main, 0.05)} 0%, transparent 50%)`,
          zIndex: 0,
        },
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          background: alpha(colors.background.overlay, 0.8),
          backdropFilter: "blur(20px)",
          borderBottom: `1px solid ${alpha(colors.secondary.main, 0.2)}`,
          color: colors.text.primary,
          py: { xs: 1.5, md: 2 },
          mb: 4,
          position: "relative",
          overflow: "hidden",
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: `linear-gradient(90deg, transparent, ${colors.accent.red}, ${colors.primary.main}, ${colors.accent.red}, transparent)`,
            animation: `${spin} 3s linear infinite`,
          }}
        />
        
        <Container maxWidth="lg">
          <Stack 
            direction={{ xs: "column", md: "row" }} 
            spacing={2} 
            alignItems="center" 
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  background: colors.primary.gradient,
                  width: { xs: 50, md: 60 },
                  height: { xs: 50, md: 60 },
                  boxShadow: `0 0 30px ${alpha(colors.primary.main, 0.3)}`,
                  transition: "transform 0.3s ease",
                  '&:hover': {
                    transform: "scale(1.05) rotate(5deg)",
                  },
                }}
              >
                <DiningIcon sx={{ fontSize: { xs: 30, md: 38 }, color: colors.text.primary }} />
              </Avatar>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 800,
                    fontSize: { xs: "1.6rem", md: "2rem" },
                    letterSpacing: 2,
                    background: `linear-gradient(135deg, ${colors.text.primary} 0%, ${colors.accent.red} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1.2,
                  }}
                >
                  GREEN VELVET
                </Typography>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: colors.text.secondary,
                    fontWeight: 400,
                    fontSize: { xs: "0.8rem", md: "0.9rem" },
                    letterSpacing: 1,
                  }}
                >
                  PREMIUM DINING EXPERIENCE
                </Typography>
              </Box>
            </Stack>
            
            <Stack direction="row" spacing={1.5}>
              {[RestaurantIcon, DrinkIcon, DiningIcon].map((Icon, index) => (
                <Avatar 
                  key={index}
                  sx={{ 
                    bgcolor: alpha(colors.primary.main, 0.15),
                    border: `1px solid ${alpha(colors.secondary.main, 0.3)}`,
                    color: colors.secondary.main,
                    width: { xs: 36, md: 40 },
                    height: { xs: 36, md: 40 },
                    backdropFilter: "blur(5px)",
                    transition: "all 0.3s ease",
                    '&:hover': {
                      bgcolor: alpha(colors.primary.main, 0.25),
                      transform: "translateY(-2px) scale(1.1)",
                      color: colors.secondary.light,
                    }
                  }}
                >
                  <Icon sx={{ fontSize: { xs: 20, md: 22 } }} />
                </Avatar>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        {/* API Error Alert */}
        {apiError && (
          <Fade in={true}>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                background: alpha("#EF5350", 0.1),
                color: "#EF5350",
                border: "1px solid rgba(239, 83, 80, 0.3)",
                backdropFilter: "blur(10px)",
                '& .MuiAlert-icon': {
                  color: "#EF5350",
                },
              }}
              onClose={() => setApiError(null)}
            >
              {apiError}
            </Alert>
          </Fade>
        )}

        <Grid container spacing={4}>
          {/* Left Column - Loyalty Form */}
          <Grid item xs={12} md={7}>
            <Grow in={true} timeout={800}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  background: alpha(colors.background.paper, 0.6),
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha(colors.secondary.main, 0.15)}`,
                  boxShadow: `0 20px 40px ${alpha(colors.background.dark, 0.4)}`,
                  position: "relative",
                  overflow: "hidden",
                  '&::before': {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background: `linear-gradient(90deg, transparent, ${colors.secondary.main}, ${colors.primary.main}, ${colors.secondary.main}, transparent)`,
                  },
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.text.primary,
                      mb: 1,
                      fontSize: "1.8rem",
                      letterSpacing: 1,
                    }}
                  >
                    Join the Club
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    Become a member and unlock exclusive privileges
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  {[
                    { name: "name", label: "Full Name", icon: PersonIcon },
                    { name: "phone", label: "Phone Number", icon: PhoneIcon, sm: 6 },
                    { name: "email", label: "Email Address", icon: EmailIcon, sm: 6 },
                    { name: "dob", label: "Date of Birth", icon: BirthdayIcon, type: "date" },
                  ].map((field) => (
                    <Grid item xs={12} sm={field.sm || 12} key={field.name}>
                      <TextField
                        fullWidth
                        name={field.name}
                        label={field.label}
                        type={field.type || "text"}
                        value={loyaltyForm[field.name]}
                        onChange={handleInputChange}
                        error={!!formErrors[field.name]}
                        helperText={formErrors[field.name]}
                        variant="outlined"
                        disabled={submitting}
                        onFocus={() => setHoveredField(field.name)}
                        onBlur={() => setHoveredField(null)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <field.icon 
                                sx={{ 
                                  color: hoveredField === field.name ? colors.secondary.main : colors.text.muted,
                                  transition: "color 0.3s ease",
                                }} 
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: colors.text.primary,
                            backgroundColor: alpha(colors.background.dark, 0.6),
                            transition: "all 0.3s ease",
                            '& fieldset': {
                              borderColor: alpha(colors.secondary.main, 0.2),
                              borderWidth: "1px",
                            },
                            '&:hover fieldset': {
                              borderColor: alpha(colors.secondary.main, 0.5),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.secondary.main,
                              borderWidth: "2px",
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: colors.text.muted,
                            '&.Mui-focused': {
                              color: colors.secondary.main,
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: "#EF5350",
                            margin: "4px 0 0",
                            fontSize: "0.75rem",
                          },
                          '& input[type="date"]': {
                            color: colors.text.primary,
                            '&::-webkit-calendar-picker-indicator': {
                              filter: "invert(0.8)",
                              opacity: 0.5,
                              transition: "opacity 0.3s ease",
                              '&:hover': {
                                opacity: 1,
                              },
                            },
                          },
                        }}
                      />
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleEnroll}
                        disabled={submitting}
                        sx={{
                          background: colors.primary.gradient,
                          color: colors.text.primary,
                          py: 2,
                          borderRadius: 2,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: `0 8px 20px ${alpha(colors.primary.main, 0.3)}`,
                          transition: "all 0.3s ease",
                          position: "relative",
                          overflow: "hidden",
                          flex: { sm: 1 },
                          opacity: submitting ? 0.7 : 1,
                          '&::before': {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "100%",
                            height: "100%",
                            background: `linear-gradient(90deg, transparent, ${alpha(colors.secondary.main, 0.2)}, transparent)`,
                            transition: "left 0.5s ease",
                          },
                          '&:hover': {
                            transform: submitting ? "none" : "translateY(-2px)",
                            boxShadow: `0 12px 30px ${alpha(colors.primary.main, 0.4)}`,
                            '&::before': {
                              left: "100%",
                            },
                          },
                        }}
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      >
                        {submitting ? "Joining..." : "Join Now"}
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={handleViewMenu}
                        sx={{
                          borderColor: colors.secondary.main,
                          color: colors.secondary.main,
                          py: 2,
                          borderRadius: 2,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          borderWidth: 2,
                          transition: "all 0.3s ease",
                          position: "relative",
                          overflow: "hidden",
                          flex: { sm: 1 },
                          background: alpha(colors.secondary.main, 0.05),
                          backdropFilter: "blur(5px)",
                          '&:hover': {
                            borderColor: colors.secondary.light,
                            backgroundColor: alpha(colors.secondary.main, 0.15),
                            transform: "translateY(-2px)",
                            boxShadow: `0 8px 20px ${alpha(colors.secondary.main, 0.3)}`,
                          },
                        }}
                        startIcon={<MenuBookIcon />}
                        endIcon={<OpenInNewIcon sx={{ fontSize: "1rem", opacity: 0.8 }} />}
                      >
                        View Our Menu
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Benefits List */}
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ 
                    my: 3,
                    borderColor: alpha(colors.secondary.main, 0.2),
                  }}>
                    <Chip 
                      label="Exclusive Benefits" 
                      sx={{ 
                        bgcolor: alpha(colors.primary.main, 0.2),
                        color: colors.secondary.main,
                        border: `1px solid ${alpha(colors.secondary.main, 0.3)}`,
                        fontWeight: 600,
                      }} 
                    />
                  </Divider>
                  
                  <Grid container spacing={2}>
                    {[
                      { icon: StarIcon, text: "Earn points on every order" },
                      { icon: LoyaltyIcon, text: "Exclusive member discounts" },
                      { icon: TrophyIcon, text: "Birthday special rewards"},
                    ].map((benefit, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            background: alpha(colors.background.dark, 0.6),
                            borderRadius: 2,
                            border: `1px solid ${alpha(colors.secondary.main, 0.1)}`,
                            transition: "all 0.3s ease",
                            '&:hover': {
                              transform: "translateY(-2px)",
                              borderColor: alpha(colors.secondary.main, 0.3),
                              boxShadow: `0 8px 16px ${alpha(colors.background.dark, 0.4)}`,
                            },
                          }}
                        >
                          <Stack spacing={1} alignItems="center">
                            <Avatar sx={{ 
                              bgcolor: alpha(colors.primary.main, 0.2),
                              border: `1px solid ${alpha(colors.secondary.main, 0.3)}`,
                              width: 40, 
                              height: 40,
                              color: colors.secondary.main,
                            }}>
                              <benefit.icon />
                            </Avatar>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: colors.text.secondary,
                                textAlign: "center",
                                fontSize: "0.8rem",
                              }}
                            >
                              {benefit.text}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Stats Summary */}
               
              </Paper>
            </Grow>
          </Grid>

          {/* Right Column - QR Code */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Zoom in={true} timeout={800}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    background: alpha(colors.background.paper, 0.6),
                    backdropFilter: "blur(20px)",
                    textAlign: "center",
                    border: `1px solid ${alpha(colors.secondary.main, 0.15)}`,
                    boxShadow: `0 20px 40px ${alpha(colors.background.dark, 0.4)}`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                    <QrCodeIcon sx={{ color: colors.secondary.main, mr: 1 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        color: colors.text.primary,
                        fontSize: "1.3rem",
                      }}
                    >
                      Download and Share
                    </Typography>
                  </Box>
                  
                  <Box
                    ref={qrRef}
                    sx={{
                      p: 3,
                      background: "#FFFFFF",
                      borderRadius: 3,
                      display: "inline-block",
                      mb: 3,
                      boxShadow: `0 15px 30px ${alpha(colors.background.dark, 0.3)}`,
                      transition: "transform 0.3s ease",
                      '&:hover': {
                        transform: "scale(1.02)",
                      },
                    }}
                  >
                    <QRCodeCanvas
                      value={qrMenuURL || "https://loyalty.myelegante.com"}
                      size={isMobile ? 180 : 200}
                      fgColor={colors.primary.dark}
                      bgColor="#FFFFFF"
                      level="H"
                      includeMargin={true}
                    />
                  </Box>
                  
                  <Button
                    variant="outlined"
                    onClick={handleDownloadQR}
                    sx={{
                      borderColor: alpha(colors.secondary.main, 0.5),
                      color: colors.secondary.main,
                      borderWidth: 1.5,
                      px: 4,
                      py: 1.5,
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      '&:hover': {
                        borderColor: colors.secondary.main,
                        backgroundColor: alpha(colors.secondary.main, 0.1),
                        borderWidth: 1.5,
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 16px ${alpha(colors.primary.main, 0.2)}`,
                      },
                    }}
                    startIcon={<DownloadIcon />}
                  >
                    Download QR Code
                  </Button>
                </Paper>
              </Zoom>
            </Stack>
          </Grid>
        </Grid>

        {/* Success Snackbar */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={4000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert 
            severity="success" 
            variant="filled"
            sx={{ 
              background: colors.primary.gradient,
              color: colors.text.primary,
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: `0 8px 20px ${alpha(colors.primary.main, 0.3)}`,
              '& .MuiAlert-icon': {
                color: colors.text.primary,
              },
            }}
          >
            Welcome to Green Velvet! You've earned 50 bonus points!
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default MainContent;